import { onlyNumeric } from '@origin-digital/normalize';
import { connect } from 'react-redux';
import { Button, Text, LinkText } from '@origin-digital/style-guide';
import { Box } from '@origin-digital/layout';
import { Field, Form, FieldArrayRenderProps } from 'formik';
import React from 'react';

import analytics from '../../../analytics';
import FormRow from '../../../components/Form/Layout/FormRow';
import SelectField from '../../../components/Form/Fields/SelectField';
import {
  ManualAddress,
  TextAreaField,
  TextField,
  AddressField,
  HiddenField,
  Checkbox
} from '../../../components/Form/Fields';
import DisqualificationAlert from '../../../components/DisqualificationAlert';
import DisqualificationProvider from '../../../components/DisqualificationProvider';
import { DisqualificationContext } from '../../../components/DisqualificationProvider/disqualification-context';

import { RequestServiceProps } from './Fields.types';
import { CHECK_REPAIR_SERVICEABILITY } from '../../../graphql/queries';
import { isFieldTouched } from '../../../utils/formHelpers';

import { updateCustomerDetail } from '../../../redux/actions/customer';
import { checkIfPostcodeServiceable } from '../../../redux/thunks';

import general from '../content.json';

const Fields = (props: RequestServiceProps): JSX.Element => {
  const {
    productType,
    validating,
    isValid,
    updateCustomerDetail,
    checkIfPostcodeServiceable,
    disqualifiedFields,
    asyncSubmitting,
    customerAddress,
    isInitialValid,
    hasManualAddress
  } = props;

  return (
    <DisqualificationProvider disqualificationMessages={general.content.disqualification}>
      <Form>
        <FormRow content={general.content.enquiryType}>
          <Field
            name={general.fields.enquiryType.name}
            component={SelectField}
            changeHandler={updateCustomerDetail}
            label={general.fields.enquiryType.label}
            options={general.fields.enquiryType.options}
          />
          <Field
            name={general.fields.comment.name}
            maxLength={3000}
            component={TextAreaField}
            changeHandler={updateCustomerDetail}
            label={general.fields.comment.label}
            analytics={false}
          />
        </FormRow>

        <FormRow content={general.content.yourDetails}>
          <Field
            name={general.fields.firstName.name}
            label={general.fields.firstName.label}
            isInitialValid={isInitialValid}
            component={TextField}
            changeHandler={updateCustomerDetail}
            analytics={false}
          />
          <Field
            name={general.fields.lastName.name}
            label={general.fields.lastName.label}
            isInitialValid={isInitialValid}
            component={TextField}
            changeHandler={updateCustomerDetail}
            analytics={false}
          />
          <Field
            name={general.fields.email.name}
            label={general.fields.email.label}
            isInitialValid={isInitialValid}
            component={TextField}
            changeHandler={updateCustomerDetail}
            analytics={false}
          />
          <Field
            name={general.fields.phone.name}
            component={TextField}
            helpText={general.fields.phone.helpText}
            isInitialValid={isInitialValid}
            label={general.fields.phone.label}
            transform={onlyNumeric}
            changeHandler={updateCustomerDetail}
            maxLength={10}
            analytics={false}
          />
        </FormRow>

        <FormRow
          content={general.content.propertyAddress}
          byline={general.content.propertyAddress.byline}
        >
          <Field
            name={general.fields.address.name}
            component={AddressField}
            label={general.fields.address.label}
            productType={productType}
            isInitialValid={isInitialValid}
            customerAddress={customerAddress}
            isDisqualified={disqualifiedFields.includes(general.fields.hiddenPostcode.name)}
            hasManualAddress={hasManualAddress}
            manualAddressPlaceholder={general.content.address.manualAddressPlaceholder}
            graphQuery={CHECK_REPAIR_SERVICEABILITY}
            validating={validating}
            changeHandler={updateCustomerDetail}
            addressCheckHandler={checkIfPostcodeServiceable}
          />
          <ManualAddress
            formData={general}
            productType={productType}
            changeHandler={updateCustomerDetail}
            isInitialValid={isInitialValid}
            hasManualAddress={hasManualAddress}
            addressCheckHandler={checkIfPostcodeServiceable}
            isDisqualified={disqualifiedFields.includes(general.fields.hiddenPostcode.name)}
            validating={validating}
            graphQuery={CHECK_REPAIR_SERVICEABILITY}
          />
          <DisqualificationContext.Consumer>
            {context => (
              <Field
                name={general.fields.hiddenPostcode.name}
                render={(helpers: FieldArrayRenderProps) => {
                  const touched: boolean = isFieldTouched(helpers.form, name);
                  return (
                    <React.Fragment>
                      <HiddenField
                        name={general.fields.hiddenPostcode.name}
                        touched={touched}
                        helpers={helpers}
                        disqualificationHandler={context.disqualificationHandler}
                      />
                      <Box marginTop={-3}>
                        <DisqualificationAlert
                          name={general.fields.hiddenPostcode.name}
                          heading={general.content.disqualification.heading}
                          message={general.content.disqualification.hiddenPostcode}
                        />
                      </Box>
                    </React.Fragment>
                  );
                }}
              />
            )}
          </DisqualificationContext.Consumer>
        </FormRow>

        <Box marginBottom={4}>
          <Text size={'sm'}>{general.content.allowMarketingByline}</Text>
        </Box>
        <Field
          name={general.fields.allowMarketingCommunications.name}
          id={general.fields.allowMarketingCommunications.name}
          component={Checkbox}
          changeHandler={updateCustomerDetail}
          label={general.fields.allowMarketingCommunications.label}
        />
        <Box marginTop={10}>
          <Button
            type="submit"
            data-id="LeaveYourDetailsButton"
            text={general.buttons.submit}
            disabled={!isValid}
            spinner={asyncSubmitting}
            onClick={() => {
              analytics.buttonClick('submit', 'General enquiry');
            }}
          />
        </Box>
        <Box marginTop={10}>
          <Text size={'sm'}>
            {general.content.privacyStatement.byline}
            <Box marginLeft={1} display={'inlineBlock'}>
              <LinkText text={general.content.privacyStatement.linkText} href={'/privacy.html'} />
            </Box>
          </Text>
        </Box>
      </Form>
    </DisqualificationProvider>
  );
};

const mapStateToProps = (state: any) => ({
  productType: state.customer.enquiryType ? state.customer.enquiryType : 'MAINTENANCE',
  hasManualAddress: state.customer.hasManualAddress ? state.customer.hasManualAddress : false,
  disqualifiedFields: state.disqualification.disqualifiedFields,
  validating: state.request.validating,
  asyncSubmitting: state.request.submitting,
  customerAddress: {
    label: state.customer.address,
    qasAddress: {
      unit: state.customer.unit ? state.customer.unit : '',
      streetNumber: state.customer.streetNumber ? state.customer.streetNumber : '',
      streetName: state.customer.streetName ? state.customer.streetName : '',
      streetType: state.customer.streetType ? state.customer.streetType : '',
      streetNameOnly: state.customer.streetNameOnly ? state.customer.streetNameOnly : '',
      suburb: state.customer.suburb ? state.customer.suburb : '',
      city: state.customer.city ? state.customer.city : '',
      state: state.customer.state ? state.customer.state : '',
      postcode: state.customer.postcode ? state.customer.postcode : '',
      hiddenPostcode: state.customer.hiddenPostcode ? state.customer.hiddenPostcode : 'true',
      label: state.customer.address ? state.customer.address : ''
    }
  }
});

export default connect(
  mapStateToProps,
  {
    updateCustomerDetail,
    checkIfPostcodeServiceable
  }
)(Fields);
